import { useMutation } from "@apollo/client";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Stack,
  TextField,
} from "@mui/material";
import { type ChangeEvent, type FormEvent, useState } from "react";
import { useNotify, useTranslate } from "react-admin";

import {
  InviteStaffDocument,
  type InviteStaffMutation,
  type InviteStaffMutationVariables,
  StaffInvitationsDocument,
  StaffRole,
} from "../../generated/graphql";
import { captureException } from "../../lib/errorHandling";
import { STAFF_ROLES, staffManagementErrorKey } from "../../lib/staffManagement";
import { EMAIL_VALIDATION_PATTERN } from "../../lib/validation";
import { dialogActionsSx, dialogContentSx, dialogTitleSx } from "../../theme/dialogStyles";

const TITLE_ID = "invite-staff-title";

type InviteStaffDialogProps = {
  open: boolean;
  onClose: () => void;
};

export const InviteStaffDialog = ({ open, onClose }: InviteStaffDialogProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [email, setEmail] = useState("");
  const [selected, setSelected] = useState<StaffRole[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [inviteStaff, { loading }] = useMutation<InviteStaffMutation, InviteStaffMutationVariables>(
    InviteStaffDocument
  );

  const trimmedEmail = email.trim();
  const canSubmit = EMAIL_VALIDATION_PATTERN.test(trimmedEmail) && selected.length > 0 && !loading;

  const handleClose = () => {
    setEmail("");
    setSelected([]);
    setError(null);
    onClose();
  };

  const handleToggle = (role: StaffRole) => (event: ChangeEvent<HTMLInputElement>) => {
    setSelected(previous =>
      event.target.checked ? [...previous, role] : previous.filter(candidate => candidate !== role)
    );
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    setError(null);
    try {
      await inviteStaff({
        variables: { email: trimmedEmail, roles: STAFF_ROLES.filter(role => selected.includes(role)) },
        refetchQueries: [{ query: StaffInvitationsDocument }],
        awaitRefetchQueries: true,
      });
    } catch (failure) {
      const key = staffManagementErrorKey(failure);
      if (key === null) captureException(failure, "staff/team: invite");
      setError(translate(key ?? "team.invite.error"));
      return;
    }
    notify(translate("team.invite.sent", { email: trimmedEmail }), { type: "success" });
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby={TITLE_ID}
      PaperProps={{ component: "form", onSubmit: handleSubmit }}
    >
      <DialogTitle id={TITLE_ID} sx={dialogTitleSx}>
        {translate("team.invite.title")}
      </DialogTitle>
      <DialogContent sx={dialogContentSx}>
        <Stack gap={2} pt={1}>
          <TextField
            name="email"
            type="email"
            label={translate("team.invite.email")}
            value={email}
            onChange={event => setEmail(event.target.value)}
            required
            autoFocus
            autoComplete="off"
            fullWidth
          />
          <FormControl component="fieldset">
            <FormLabel component="legend">{translate("team.invite.roles")}</FormLabel>
            <FormGroup>
              {STAFF_ROLES.map(role => (
                <FormControlLabel
                  key={role}
                  control={<Checkbox checked={selected.includes(role)} onChange={handleToggle(role)} />}
                  label={translate(`staffRoles.${role}`)}
                />
              ))}
            </FormGroup>
            <FormHelperText>{translate("team.invite.rolesHelp")}</FormHelperText>
          </FormControl>
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={dialogActionsSx}>
        <Button type="button" onClick={handleClose}>
          {translate("team.invite.cancel")}
        </Button>
        <Button type="submit" variant="contained" disabled={!canSubmit} data-testid="invite-staff-submit">
          {translate("team.invite.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
